/**
 * Where a branch's menu comes from — the `menu_source` field on
 * `PATCH/POST /restaurants`.
 *
 * NO API SOURCE for the LIST: the wire types `menu_source` as a bare string
 * (`v1Restaurant.menu_source`, and the same on both write bodies), and no
 * endpoint enumerates the valid values. These four are the set the backend
 * actually accepts, supplied by the team; the wire values are sent verbatim.
 *
 * `internal` is the only one that gets translated — it names Noor's own
 * catalogue, so it reads as a word in each locale. The other three are product
 * names (Delever, IKO, La Cafe), which stay as they are in every language:
 * translating a vendor's name would misname it.
 *
 * When an endpoint starts serving this catalogue, replace this module with a
 * real query — the two form fields read it through `MENU_SOURCES` only.
 */
export const MENU_SOURCES = ['internal', 'delever', 'iko', 'la_cafe'] as const;

export type MenuSource = (typeof MENU_SOURCES)[number];

/**
 * The display label for a menu source.
 *
 * Returns an i18n KEY for `internal` and a literal for the three vendor names,
 * which is why callers go through {@link menuSourceLabel} rather than
 * translating blindly.
 */
const VENDOR_LABELS: Partial<Record<MenuSource, string>> = {
  delever: 'Delever',
  iko: 'IKO',
  la_cafe: 'La Cafe',
};

const DISPLAY_LABELS: Record<string, string> = {
  delever: 'Delever',
  iko: 'IKO',
  iiko: 'iiko',
  la_cafe: 'La Cafe',
};

/**
 * Resolve one source's label. `translate` is only consulted for the localized
 * member, so a vendor name can never be accidentally run through i18n.
 */
export function menuSourceLabel(
  source: MenuSource,
  translate: (key: string) => string,
): string {
  return VENDOR_LABELS[source] ?? translate('branches.form.menuSources.internal');
}

/**
 * Resolve a read-side menu source label.
 *
 * Writes use the small `MENU_SOURCES` vocabulary, but merchant capabilities
 * have also been observed with `manual` / `iiko`. Display must tolerate both
 * without blanking the value.
 */
export function menuSourceDisplayLabel(
  source: string | undefined,
  translate: (key: string) => string,
): string {
  const normalized = source?.trim();
  if (!normalized) return '';
  if (normalized === 'internal' || normalized === 'manual') {
    return translate('branches.form.menuSources.internal');
  }
  return DISPLAY_LABELS[normalized] ?? normalized;
}

/** Every source as a `{ value, label }` pair, in the order the select lists them. */
export function menuSourceOptions(
  translate: (key: string) => string,
): { value: MenuSource; label: string }[] {
  return MENU_SOURCES.map((value) => ({
    value,
    label: menuSourceLabel(value, translate),
  }));
}
